import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { useScopedI18n } from "@/locales/client";
import { countries } from "@talentir/shared/countries";
import { Check, ChevronDown } from "lucide-react";
import { useState } from "react";

export function CountrySelect({
    className,
    value,
    onChange,
}: {
    className?: string;
    value: string;
    onChange: (value: string) => void;
}) {
    const [open, setOpen] = useState<boolean>(false);

    const t = useScopedI18n("countries");

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <button
                    aria-expanded={open}
                    type="button"
                    className={cn(
                        "flex h-9 w-full min-w-0 items-center justify-between rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-xs outline-none dark:bg-input/30",
                        "focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50",
                        "aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40",
                        className,
                    )}
                >
                    {value ? (
                        <span className="flex min-w-0 items-center gap-2">
                            <span className="text-lg leading-none">
                                {countries.find((country) => country.alpha2 === value)?.flag}
                            </span>
                            <span className="truncate">{t(value as keyof typeof t)}</span>
                        </span>
                    ) : (
                        <span className="text-muted-foreground">Select country</span>
                    )}
                    <ChevronDown
                        size={16}
                        strokeWidth={2}
                        className="shrink-0 text-muted-foreground/80"
                        aria-hidden="true"
                    />
                </button>
            </PopoverTrigger>
            <PopoverContent className="w-full min-w-[var(--radix-popper-anchor-width)] p-0" align="start" side="top">
                <Command>
                    <CommandInput placeholder="Search country..." />
                    <CommandList>
                        <CommandEmpty>No country found.</CommandEmpty>
                        <CommandGroup heading={"Countries"}>
                            {countries.map((country) => (
                                <CommandItem
                                    key={country.alpha2}
                                    value={t(country.alpha2)}
                                    onSelect={() => {
                                        onChange(country.alpha2);
                                        setOpen(false);
                                    }}
                                >
                                    <span className="mr-2 text-lg leading-none">{country.flag}</span>{" "}
                                    {t(country.alpha2)}
                                    <Check
                                        className={cn(
                                            "ml-auto",
                                            value === country.alpha2 ? "opacity-100" : "opacity-0",
                                        )}
                                    />
                                </CommandItem>
                            ))}
                        </CommandGroup>
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
}
